import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Contact, Search, Plus, Trash2, Phone, Mail, Pencil, ExternalLink, Settings } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", vendor_type: "", jurisdictions: "", rate: "", location: "", phone: "", email: "", notes: "" };
const FIELDS = [["name", "Name"], ["vendor_type", "Type"], ["jurisdictions", "Jurisdictions"], ["rate", "Rate"], ["location", "Location"], ["phone", "Phone"], ["email", "Email"]];

export default function Vendors() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const isAdmin = user?.role === "admin";
  const [listUrl, setListUrl] = useState("");
  const [urlOpen, setUrlOpen] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  const load = () => api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/settings").then((r) => setListUrl(r.data.vendor_list_url || "")).catch(() => {}); }, []);
  const saveUrl = async () => {
    try { await api.put("/settings/vendor_list_url", { value: urlDraft }); setListUrl(urlDraft); setUrlOpen(false); toast.success("Microsoft List link saved"); }
    catch { toast.error("Could not save link"); }
  };

  const types = useMemo(() => ["all", ...Array.from(new Set(vendors.map((v) => v.vendor_type).filter(Boolean)))], [vendors]);
  const filtered = vendors.filter((v) => {
    const mQ = !q || `${v.name} ${v.vendor_type} ${v.jurisdictions} ${v.location} ${v.notes}`.toLowerCase().includes(q.toLowerCase());
    const mT = type === "all" || v.vendor_type === type;
    return mQ && mT;
  });

  const openNew = () => { setEditId(null); setForm(BLANK); setOpen(true); };
  const openEdit = (v) => { setEditId(v.id); setForm({ ...BLANK, ...v }); setOpen(true); };
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editId) { await api.put(`/vendors/${editId}`, form); toast.success("Vendor updated"); }
      else { await api.post("/vendors", form); toast.success("Vendor added"); }
      setOpen(false); setForm(BLANK); setEditId(null); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { await api.delete(`/vendors/${id}`); load(); toast.success("Removed"); };

  return (
    <div data-testid="vendors-page">
      <PageHeader overline="Directory" title="Vendors & Experts" subtitle="Court reporters, process servers, experts, interpreters, mediators and litigation support — filterable.">
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="open-ms-list-btn" disabled={!listUrl} onClick={() => listUrl && window.open(listUrl, "_blank", "noopener")}>
            <ExternalLink className="h-4 w-4 mr-1.5" /> Open in Microsoft Lists
          </Button>
          {isAdmin && (
            <Dialog open={urlOpen} onOpenChange={(o) => { setUrlOpen(o); if (o) setUrlDraft(listUrl); }}>
              <DialogTrigger asChild><Button variant="ghost" size="icon" data-testid="edit-ms-list-btn" title="Set Microsoft List URL"><Settings className="h-4 w-4" /></Button></DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Microsoft List Link</DialogTitle><DialogDescription className="sr-only">Set the vendor Microsoft List URL.</DialogDescription></DialogHeader>
                <div className="space-y-2">
                  <Label className="text-xs">Microsoft List URL</Label>
                  <Input data-testid="ms-list-url-input" value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} placeholder="https://…sharepoint.com/…/Lists/Vendors" />
                  <p className="text-xs text-muted-foreground">Paste the SharePoint / Microsoft Lists link ops uses to maintain the vendor &amp; expert directory.</p>
                </div>
                <DialogFooter><Button data-testid="save-ms-list-url-btn" onClick={saveUrl}>Save Link</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {editor && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(BLANK); } }}>
            <DialogTrigger asChild><Button data-testid="add-vendor-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add Vendor</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editId ? "Edit Vendor / Expert" : "Add Vendor / Expert"}</DialogTitle><DialogDescription className="sr-only">Vendor details form.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map(([k, l]) => (
                  <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`vendor-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
                ))}
                <div className="col-span-2"><Label className="text-xs">Notes</Label><Input data-testid="vendor-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="save-vendor-btn" onClick={save}>{editId ? "Save Changes" : "Save"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="vendors-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors, specialties, jurisdictions…" className="pl-9" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-64" data-testid="vendors-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All Types" : t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Contact} title="No vendors found" />
      ) : (
        <div className="border border-border rounded-sm overflow-hidden" data-testid="vendors-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60">
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Jurisdictions</TableHead>
                <TableHead>Rate</TableHead><TableHead>Contact</TableHead><TableHead>Notes</TableHead>
                {editor && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((v) => (
                <TableRow key={v.id} data-testid="vendor-row">
                  <TableCell className="font-medium">{v.name}<div className="text-xs text-muted-foreground">{v.location}</div></TableCell>
                  <TableCell><span className="text-[11px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm whitespace-nowrap">{v.vendor_type}</span></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{v.jurisdictions}</TableCell>
                  <TableCell className="text-sm font-medium">{v.rate}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{v.phone}</div>
                    <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{v.email}</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px]">{v.notes}</TableCell>
                  {editor && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button data-testid="edit-vendor-btn" onClick={() => openEdit(v)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                        <button data-testid="delete-vendor-btn" onClick={() => remove(v.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
